import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link } from "react-router";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/lib/hooks/useAuth";
import {
  APP_STRIP_ITEMS,
  COMPARISON_SECTIONS,
  FAQS,
  FOOTER_GROUPS,
  HERO_CONTENT,
  LANDING_NAV_ITEMS,
  PRICING_PLANS,
  PRIVACY_CARDS,
  TIME_SAVED,
  WHY_SLIDE,
  type AppStripItem,
  type DictationDemoData,
  type PricingPlanData,
} from "./content";
import { LandingNav } from "./LandingNav";

const NAV_SCROLL_OFFSET = 118;
type DictationPhase = "empty" | "placeholders" | "partial" | "full" | "reset";
type DictationWordState = "hidden" | "covered" | "resolved";

interface DictationLoopSnapshot {
  prefersReducedMotion: boolean;
  phase: DictationPhase;
  phaseProgress: number;
  rightElapsedMs: number;
  leftElapsedMs: number;
}

interface DictationWordMeasurement {
  width: number;
  height: number;
}

interface DictationWordData {
  id: string;
  paragraphIndex: number;
  globalIndex: number;
  text: string;
  separator: string;
}

interface DictationMeasuredWord extends DictationWordData, DictationWordMeasurement {}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getDictationTotalDuration(timings: DictationDemoData["timings"]) {
  return timings.emptyMs + timings.placeholdersMs + timings.partialMs + timings.fullMs + timings.resetMs;
}

function getDictationPhase(elapsedMs: number, timings: DictationDemoData["timings"]) {
  const phases: Array<{ phase: DictationPhase; duration: number }> = [
    { phase: "empty", duration: timings.emptyMs },
    { phase: "placeholders", duration: timings.placeholdersMs },
    { phase: "partial", duration: timings.partialMs },
    { phase: "full", duration: timings.fullMs },
    { phase: "reset", duration: timings.resetMs },
  ];

  let cursor = 0;
  for (const entry of phases) {
    if (elapsedMs < cursor + entry.duration) {
      return {
        phase: entry.phase,
        phaseProgress: entry.duration === 0 ? 1 : (elapsedMs - cursor) / entry.duration,
      };
    }
    cursor += entry.duration;
  }

  return { phase: "reset" as const, phaseProgress: 1 };
}

function buildParagraphWordMatrix(paragraphs: string[]): DictationWordData[][] {
  let globalIndex = 0;

  return paragraphs.map((paragraph, paragraphIndex) =>
    paragraph
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((word, wordIndex, words) => {
        const entry = {
          id: `${paragraphIndex}-${wordIndex}-${globalIndex}`,
          paragraphIndex,
          globalIndex,
          text: word,
          separator: wordIndex < words.length - 1 ? " " : "",
        };

        globalIndex += 1;
        return entry;
      }),
  );
}

interface TypingStep {
  at: number;
  len: number;
  extra: string;
}

function typingJitter(seed: number) {
  let x = (seed + 0x9e3779b9) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  return ((x ^ (x >>> 16)) >>> 0) / 4294967295;
}

// A human keystroke performance: uneven key intervals, breathing room after
// punctuation, and two transposition typos that get noticed, erased and
// retyped. Seeded by character index so every loop replays identically; the
// caller scales the timeline into the loop's fixed typing window.
function buildTypingTimeline(text: string) {
  const steps: TypingStep[] = [{ at: 0, len: 0, extra: "" }];
  let t = 0;

  const words: Array<{ start: number; text: string }> = [];
  const wordPattern = /[A-Za-zÀ-ÿ]{6,}/g;
  for (let match = wordPattern.exec(text); match; match = wordPattern.exec(text)) {
    words.push({ start: match.index, text: match[0] });
  }
  const typoWords = new Map<number, string>();
  for (const target of [text.length / 3, (text.length * 2) / 3]) {
    let best: { start: number; text: string } | undefined;
    for (const word of words) {
      if (typoWords.has(word.start)) continue;
      if (!best || Math.abs(word.start - target) < Math.abs(best.start - target)) best = word;
    }
    if (best) typoWords.set(best.start, best.text);
  }

  for (let i = 0; i < text.length; i += 1) {
    const typoWord = typoWords.get(i);
    if (typoWord) {
      const wrong = (typoWord[0] + typoWord[2] + typoWord[1] + typoWord.slice(3)).slice(0, 4);
      for (let k = 0; k < wrong.length; k += 1) {
        t += 55 + typingJitter(i * 31 + k) * 55;
        steps.push({ at: t, len: i, extra: wrong.slice(0, k + 1) });
      }
      t += 420;
      for (let k = wrong.length - 1; k >= 0; k -= 1) {
        t += 45;
        steps.push({ at: t, len: i, extra: wrong.slice(0, k) });
      }
      t += 170;
    }
    const prev = i > 0 ? text[i - 1] : "";
    let delay = 34 + typingJitter(i) * 52;
    if (prev === ".") delay += 340;
    else if (prev === ",") delay += 170;
    else if (prev === " " && typingJitter(i * 7 + 3) < 0.15) delay += 240;
    t += delay;
    steps.push({ at: t, len: i + 1, extra: "" });
  }

  return { steps, totalMs: t };
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);

    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  return prefersReducedMotion;
}

function getDictationWordState(
  word: Pick<DictationWordData, "globalIndex">,
  coveredWordCount: number,
  resolvedWordCount: number,
): DictationWordState {
  if (word.globalIndex < resolvedWordCount) {
    return "resolved";
  }

  if (word.globalIndex < coveredWordCount) {
    return "covered";
  }

  return "hidden";
}

function getDictationWordMaskStyle(word: DictationMeasuredWord): CSSProperties | undefined {
  if (!word.width || !word.height) {
    return undefined;
  }

  const horizontalInset = Math.min(4, Math.max(1, Math.round(word.width * 0.045)));
  const verticalInset = Math.min(5, Math.max(2, Math.round(word.height * 0.2)));
  const maskWidth = Math.max(10, word.width - horizontalInset * 2);
  const maskHeight = Math.max(12, word.height - verticalInset * 2);

  return {
    width: `${maskWidth}px`,
    height: `${Math.round(maskHeight * 1.2)}px`,
  };
}

function useMeasuredWordMatrix(paragraphWordMatrix: DictationWordData[][]) {
  const wordEntries = useMemo(() => paragraphWordMatrix.flat(), [paragraphWordMatrix]);
  const wordRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const [wordMeasurements, setWordMeasurements] = useState<Record<string, DictationWordMeasurement>>({});

  const registerWordRef = useCallback((wordId: string, node: HTMLSpanElement | null) => {
    if (node) {
      wordRefs.current[wordId] = node;
      return;
    }

    delete wordRefs.current[wordId];
  }, []);

  useLayoutEffect(() => {
    if (!wordEntries.length) {
      setWordMeasurements({});
      return undefined;
    }

    let cancelled = false;
    let resizeFrame = 0;

    const measureWords = () => {
      if (cancelled) return;

      const nextMeasurements: Record<string, DictationWordMeasurement> = {};
      for (const word of wordEntries) {
        const node = wordRefs.current[word.id];
        if (!node) continue;

        const rect = node.getBoundingClientRect();
        nextMeasurements[word.id] = {
          width: Math.ceil(Math.max(node.offsetWidth, rect.width)),
          height: Math.ceil(Math.max(node.offsetHeight, rect.height)),
        };
      }

      setWordMeasurements((currentMeasurements) => {
        const currentKeys = Object.keys(currentMeasurements);
        const nextKeys = Object.keys(nextMeasurements);
        const didChange =
          currentKeys.length !== nextKeys.length ||
          nextKeys.some(
            (key) =>
              currentMeasurements[key]?.width !== nextMeasurements[key]?.width ||
              currentMeasurements[key]?.height !== nextMeasurements[key]?.height,
          );

        return didChange ? nextMeasurements : currentMeasurements;
      });
    };

    const handleResize = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(measureWords);
    };

    const waitForFonts = async () => {
      if ("fonts" in document) {
        await document.fonts.ready;
      }

      measureWords();
    };

    void waitForFonts();
    window.addEventListener("resize", handleResize);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener("resize", handleResize);
    };
  }, [wordEntries]);

  const measuredParagraphWordMatrix = useMemo(
    () =>
      paragraphWordMatrix.map((paragraphWords) =>
        paragraphWords.map((word) => {
          const measurement = wordMeasurements[word.id];

          return {
            ...word,
            width: measurement?.width ?? 0,
            height: measurement?.height ?? 0,
          };
        }),
      ),
    [paragraphWordMatrix, wordMeasurements],
  );

  return {
    measuredParagraphWordMatrix,
    totalWordCount: wordEntries.length,
    registerWordRef,
  };
}

function useDictationLoop(dictationDemo?: DictationDemoData): DictationLoopSnapshot | null {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [cycleElapsedMs, setCycleElapsedMs] = useState(0);

  const totalDurationMs = useMemo(
    () => (dictationDemo ? getDictationTotalDuration(dictationDemo.timings) : 0),
    [dictationDemo],
  );

  useEffect(() => {
    if (!dictationDemo || prefersReducedMotion) {
      setCycleElapsedMs(0);
      return undefined;
    }

    let animationFrame = 0;
    const startedAt = performance.now();

    const tick = (now: number) => {
      setCycleElapsedMs((now - startedAt) % totalDurationMs);
      animationFrame = window.requestAnimationFrame(tick);
    };

    animationFrame = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(animationFrame);
  }, [dictationDemo, prefersReducedMotion, totalDurationMs]);

  if (!dictationDemo) return null;

  const finalReadableElapsed =
    dictationDemo.timings.emptyMs +
    dictationDemo.timings.placeholdersMs +
    dictationDemo.timings.partialMs +
    dictationDemo.timings.fullMs -
    1;

  const rightElapsedMs = prefersReducedMotion ? finalReadableElapsed : cycleElapsedMs;
  const leftElapsedMs = prefersReducedMotion
    ? finalReadableElapsed
    : Math.max(0, cycleElapsedMs - dictationDemo.timings.leftDelayMs);
  const { phase, phaseProgress } = prefersReducedMotion
    ? { phase: "full" as const, phaseProgress: 1 }
    : getDictationPhase(rightElapsedMs, dictationDemo.timings);

  return {
    prefersReducedMotion,
    phase,
    phaseProgress,
    rightElapsedMs,
    leftElapsedMs,
  };
}

function SectionHeading({
  title,
  tone = "onLight",
}: {
  title: string;
  tone?: "onDark" | "onLight";
}) {
  return (
    <div className="text-center">
      <h2
        className={`scroll-mt-24 text-[28px] font-semibold leading-[1.05] sm:text-[36px] sm:leading-[1.02] md:text-[50px] ${
          tone === "onLight" ? "text-[var(--cs-title-navy)]" : "text-white"
        }`}
      >
        {title}
      </h2>
    </div>
  );
}

function ComparisonLabel({
  title,
  subtitle,
  tone = "default",
}: {
  title: string;
  subtitle: string;
  tone?: "default" | "muted";
}) {
  return (
    <div className="mb-4 flex min-h-[44px] flex-col items-center justify-end text-center sm:min-h-0 sm:block">
      <p className={tone === "muted" ? "text-[13px] font-medium text-[var(--cs-navy)] opacity-70 sm:text-[18px]" : "text-[13px] font-semibold text-[var(--cs-navy)] sm:text-[18px]"}>
        {title}
      </p>
      {subtitle ? <p className="text-[11px] leading-5 text-[var(--cs-navy)] opacity-70 sm:text-[14px] sm:leading-6">{subtitle}</p> : null}
    </div>
  );
}

function RawTextFrame({
  body,
  dictationDemo,
  loopSnapshot,
}: {
  body: string;
  dictationDemo?: DictationDemoData;
  loopSnapshot?: DictationLoopSnapshot | null;
}) {
  const frameClassName =
    "relative rounded-[28px] border border-[#EEF4F8] bg-white shadow-[0_18px_48px_rgb(0_0_0_/_0.10)]";

  const timeline = useMemo(
    () => (dictationDemo ? buildTypingTimeline(dictationDemo.leftTypedText) : null),
    [dictationDemo],
  );

  if (dictationDemo && loopSnapshot && timeline) {
    const typingWindowMs =
      dictationDemo.timings.placeholdersMs + dictationDemo.timings.partialMs + 500;
    const elapsedMs = clamp(
      loopSnapshot.leftElapsedMs - dictationDemo.timings.emptyMs * 0.35,
      0,
      typingWindowMs,
    );
    const scaledMs = (elapsedMs / typingWindowMs) * timeline.totalMs;
    let lo = 0;
    let hi = timeline.steps.length - 1;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (timeline.steps[mid].at <= scaledMs) lo = mid;
      else hi = mid - 1;
    }
    const step = timeline.steps[lo];
    const visibleText = loopSnapshot.prefersReducedMotion
      ? dictationDemo.leftTypedText
      : dictationDemo.leftTypedText.slice(0, step.len) + step.extra;
    const showCaret = !loopSnapshot.prefersReducedMotion && elapsedMs < typingWindowMs;

    return (
      <div className={`${frameClassName} h-[460px] px-3 py-4 text-left sm:h-[430px] sm:px-8 sm:py-8`}>
        <div className="min-h-[380px] max-w-[520px] whitespace-pre-wrap text-[11px] leading-[1.55] text-black sm:min-h-[272px] sm:text-[14px] sm:leading-[1.6]">
          {visibleText}
          {showCaret ? <span className="cs-typing-caret ml-0.5 inline-block h-[1.15em] w-[2px] align-[-0.18em] bg-[#111111]" /> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`${frameClassName} h-full min-h-[290px] px-8 py-7 text-left`}>
      <div className="absolute right-7 top-7 h-3 w-3 rounded-full bg-[#EEF4F8]" />
      <div className="max-w-[320px] space-y-2.5 text-[16px] leading-7 text-black">
        {body.split("\n").map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    </div>
  );
}

function DictationPill({ mode }: { mode: "hidden" | "active" | "idle" }) {
  const dots = Array.from({ length: 6 }, (_, index) => index);
  const hidden = mode === "hidden";
  const idle = mode === "idle";

  return (
    <div
      className={`inline-flex h-[29px] w-[140px] items-center justify-center gap-2 rounded-[14px] bg-[#0c3f6b] px-3 transition-all duration-500 ${
        hidden ? "translate-y-4 scale-95 opacity-0" : "translate-y-0 scale-100 opacity-100"
      }`}
      aria-label="Dictaat opname actief"
    >
      <div className="flex h-[18px] items-center gap-1" aria-hidden="true">
        {dots.map((dot) => (
          <span
            key={dot}
            className={`cs-widget-pill-dot ${idle ? "cs-widget-pill-dot-idle" : ""}`}
            style={{ animationDelay: `${dot * 0.11}s` }}
          />
        ))}
      </div>
      <span className="min-w-0 truncate text-[11px] font-semibold leading-none text-white">
        Dictaat
      </span>
    </div>
  );
}

function StructuredFrame({
  variant,
  dictationDemo,
  loopSnapshot,
  transcriptLabel = "Transcript",
  structuredLabel = "SOEP",
  contentVisible = true,
}: {
  variant: "capture" | "dictation";
  dictationDemo?: DictationDemoData;
  loopSnapshot?: DictationLoopSnapshot | null;
  transcriptLabel?: string;
  structuredLabel?: string;
  contentVisible?: boolean;
}) {
  const paragraphWordMatrix = useMemo(
    () => (dictationDemo ? buildParagraphWordMatrix(dictationDemo.rightParagraphs) : []),
    [dictationDemo],
  );
  const { measuredParagraphWordMatrix, totalWordCount, registerWordRef } = useMeasuredWordMatrix(paragraphWordMatrix);
  const hasAnimatedToggle =
    variant === "capture" && transcriptLabel === "Dictaat" && structuredLabel === "SOEP" && dictationDemo && loopSnapshot;
  const comparisonFrameClassName =
    "relative h-[460px] overflow-hidden rounded-[28px] border border-[#EEF4F8] bg-white px-3 pb-6 pt-4 shadow-[0_18px_48px_rgb(0_0_0_/_0.10)] sm:h-[430px] sm:px-7 sm:pb-10 sm:pt-7";
  const soapSections = [
    {
      label: "Subjectief",
      body:
        "78-jarige patiënte op consult wegens twee weken progressieve inspanningsdyspneu en recidiverende palpitaties. Geen thoracale pijn of syncope, wel duidelijke daling van de inspanningstolerantie en enkeloedeem tegen de avond.",
    },
    {
      label: "Objectief",
      body:
        "Bij onderzoek onregelmatige tachycardie rond 128 per minuut, bloeddruk 138 over 84 en saturatie 97 procent in kamerlucht. ECG bevestigt voorkamerfibrillatie met snelle ventriculaire respons. Labo, NT-proBNP en transthoracale echocardiografie aangevraagd.",
    },
    {
      label: "Evaluatie",
      body: "Voorkamerfibrillatie met snelle ventriculaire respons. Evaluatie van structureel hartlijden en volume-status lopende.",
    },
    {
      label: "Plan",
      body: "Opstart rate control met bisoprolol, anticoagulatiebeleid beoordelen op basis van CHA2DS2-VASc en HAS-BLED, huisartsbrief vandaag nog uitsturen. Secretariaat plant echocardiografie en controleconsult binnen zes weken.",
    },
  ];

  if (variant === "capture") {
    const soapOpacity = hasAnimatedToggle
      ? loopSnapshot.prefersReducedMotion
        ? 1
        : loopSnapshot.phase === "full"
          ? clamp((loopSnapshot.phaseProgress - 0.28) / 0.08, 0, 1)
          : loopSnapshot.phase === "reset"
            ? 1
            : 0
      : contentVisible
        ? 1
        : 0;
    const visibleSoapSections = hasAnimatedToggle
      ? loopSnapshot.prefersReducedMotion
        ? soapSections.length
        : loopSnapshot.phase === "full"
          ? loopSnapshot.phaseProgress < 0.30
            ? 0
            : loopSnapshot.phaseProgress < 0.37
              ? 1
              : loopSnapshot.phaseProgress < 0.44
                ? 2
                : loopSnapshot.phaseProgress < 0.51
                  ? 3
                  : soapSections.length
          : loopSnapshot.phase === "reset"
            ? soapSections.length
            : 0
      : contentVisible
        ? soapSections.length
        : 0;
    const soapTransform = "translateY(0px)";
    const loadingOpacity = hasAnimatedToggle
      ? loopSnapshot.prefersReducedMotion
        ? 0
        : loopSnapshot.phase === "full"
          ? 1 - clamp((loopSnapshot.phaseProgress - 0.18) / 0.12, 0, 1)
          : loopSnapshot.phase === "reset"
            ? 0
            : 1
      : contentVisible
        ? 0
        : 1;
    const showSpinner = hasAnimatedToggle
      ? !loopSnapshot.prefersReducedMotion &&
        loopSnapshot.rightElapsedMs >= 2000 &&
        (loopSnapshot.phase === "empty" ||
          loopSnapshot.phase === "placeholders" ||
          (loopSnapshot.phase === "partial" && loopSnapshot.phaseProgress < 0.5))
      : !contentVisible;
    const loadingSkeleton = [
      [[118, 174, 142], [164, 132, 96]],
      [[126, 154, 108], [178, 118, 146]],
      [[138, 164], [114]],
      [[166, 148, 104], [132, 118]],
    ];
    const visibleLoadingSections = hasAnimatedToggle
      ? loopSnapshot.prefersReducedMotion
        ? 0
        : loopSnapshot.phase === "empty"
          ? 0
          : loopSnapshot.phase === "placeholders"
            ? 0
            : loopSnapshot.phase === "partial"
              ? loopSnapshot.phaseProgress < 0.5
                ? 0
                : loopSnapshot.phaseProgress < 0.68
                  ? 1
                  : loopSnapshot.phaseProgress < 0.82
                    ? 2
                    : loopSnapshot.phaseProgress < 0.92
                      ? 3
                      : 4
              : loopSnapshot.phase === "full"
                ? soapOpacity > 0.02
                  ? 0
                  : 4
              : 0
      : contentVisible
        ? 0
        : soapSections.length;

    return (
      <div className={comparisonFrameClassName}>
        <div className="relative h-full overflow-hidden">
          {showSpinner ? (
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center"
              style={{ opacity: 0.9 }}
            >
              <span className="cs-loading-spinner" />
            </div>
          ) : null}
          {loadingOpacity > 0.02 && visibleLoadingSections > 0 ? (
            <div
              aria-hidden="true"
              className="absolute inset-0 overflow-hidden"
              style={{ opacity: loadingOpacity }}
            >
              <div className="space-y-5 pt-1">
                {soapSections.slice(0, visibleLoadingSections).map((section, sectionIndex) => (
                  <div key={`soap-loading-${section.label}`} className="space-y-2">
                    <div className="h-4 w-24 rounded-full bg-[#EEF4F8]" />
                    <div className="space-y-2">
                      {loadingSkeleton[sectionIndex]?.map((row, rowIndex) => (
                        <div key={`soap-loading-row-${section.label}-${rowIndex}`} className="flex flex-wrap gap-2">
                          {row.map((width, chipIndex) => (
                            <span
                              key={`soap-loading-chip-${section.label}-${rowIndex}-${chipIndex}`}
                              className="cs-loading-block h-5 rounded-full bg-[#EEF4F8]"
                              style={{ width }}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div
            className={`cs-soep-scroll h-full overflow-y-auto pr-2 text-[11px] leading-[1.55] text-black transition-all duration-300 sm:text-[14px] sm:leading-[1.6] ${contentVisible || hasAnimatedToggle ? "" : "invisible"}`}
            style={{ opacity: soapOpacity, transform: soapTransform }}
            aria-hidden={!contentVisible && !hasAnimatedToggle}
          >
            <div className="space-y-4">
              {soapSections.map((section, sectionIndex) => (
                <p
                  key={section.label}
                  className="max-w-[560px] transition-all duration-200"
                  style={{
                    opacity: sectionIndex < visibleSoapSections ? 1 : 0,
                    transform: `translateY(${sectionIndex < visibleSoapSections ? 0 : 8}px)`,
                  }}
                >
                  <strong className="font-semibold text-[#111111]">{section.label}: </strong>
                  {section.body}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dictationDemo || !loopSnapshot) {
    return null;
  }

  const coveredWordCount = loopSnapshot.prefersReducedMotion
    ? totalWordCount
    : loopSnapshot.phase === "empty"
      ? 0
      : loopSnapshot.phase === "placeholders"
        ? Math.ceil(totalWordCount * loopSnapshot.phaseProgress)
        : totalWordCount;
  const resolvedParagraphCount = loopSnapshot.prefersReducedMotion
    ? measuredParagraphWordMatrix.length
    : loopSnapshot.phase === "partial"
      ? Math.floor(measuredParagraphWordMatrix.length * loopSnapshot.phaseProgress)
      : loopSnapshot.phase === "full" || loopSnapshot.phase === "reset"
        ? measuredParagraphWordMatrix.length
        : 0;
  const pillMode = loopSnapshot.prefersReducedMotion
    ? "idle"
    : loopSnapshot.phase === "empty" || loopSnapshot.phase === "partial" || loopSnapshot.phase === "full" || loopSnapshot.phase === "reset"
      ? "hidden"
      : "active";
  const fullProgress = loopSnapshot.phase === "full" ? loopSnapshot.phaseProgress : loopSnapshot.phase === "reset" ? 1 : 0;
  const showGlassTransition =
    !loopSnapshot.prefersReducedMotion && loopSnapshot.phase === "full" && fullProgress >= 0.16 && fullProgress < 0.5;
  const glassProgress = showGlassTransition ? clamp((fullProgress - 0.16) / 0.34, 0, 1) : 0;
  const glassIntroProgress = clamp(glassProgress / 0.22, 0, 1);
  const showPreSoapPulse = showGlassTransition && glassProgress >= 0.22;
  const structuredNotesOpacity = loopSnapshot.prefersReducedMotion
    ? 1
    : loopSnapshot.phase === "full"
      ? clamp((fullProgress - 0.48) / 0.07, 0, 1)
      : loopSnapshot.phase === "reset"
        ? 1
        : 0;
  const visibleStructuredSections = loopSnapshot.prefersReducedMotion
    ? soapSections.length
    : loopSnapshot.phase === "full"
      ? structuredNotesOpacity > 0.02
        ? soapSections.length
        : 0
      : loopSnapshot.phase === "reset"
        ? soapSections.length
        : 0;
  const dictationIntroStyle =
    structuredNotesOpacity > 0.02
      ? { opacity: 0, filter: "blur(0px)" }
      : showGlassTransition && !showPreSoapPulse
        ? {
            opacity: 1 - 0.18 * glassIntroProgress,
            filter: `blur(${0.9 * glassIntroProgress}px) brightness(${1 + 0.1 * glassIntroProgress})`,
          }
        : undefined;
  const overlayIntroOpacity = showGlassTransition && !showPreSoapPulse ? 0.62 * glassIntroProgress : undefined;
  const getParagraphWordState = (paragraphIndex: number, word: DictationMeasuredWord): DictationWordState =>
    paragraphIndex < resolvedParagraphCount ? "resolved" : getDictationWordState(word, coveredWordCount, 0);
  const lastResolvedWordId = loopSnapshot.prefersReducedMotion || resolvedParagraphCount === 0
    ? null
    : measuredParagraphWordMatrix[resolvedParagraphCount - 1]?.at(-1)?.id ?? null;

  return (
    <div className={comparisonFrameClassName}>
      <div
        className={`relative z-10 min-h-[260px] overflow-hidden text-left ${showPreSoapPulse ? "cs-pre-soep-pulse" : ""}`}
        style={dictationIntroStyle}
      >
        <div className="space-y-4 text-[11px] leading-[1.55] text-black sm:text-[14px] sm:leading-[1.6]">
          {measuredParagraphWordMatrix.map((paragraphWords, paragraphIndex) => (
            <p key={`dictation-paragraph-${paragraphIndex}`} className="max-w-[560px]">
              {paragraphWords.map((word) => {
                const wordState = getParagraphWordState(paragraphIndex, word);

                return (
                  <Fragment key={word.id}>
                    <span className="cs-dictation-word-shell">
                      <span
                        aria-hidden="true"
                        className={`cs-dictation-word-mask ${wordState === "covered" ? "is-covered" : ""}`}
                        style={getDictationWordMaskStyle(word)}
                      />
                      <span
                        className={`cs-dictation-word-copy ${
                          wordState === "resolved" ? "is-resolved" : wordState === "covered" ? "is-covered" : "is-hidden"
                        }`}
                        ref={(node) => registerWordRef(word.id, node)}
                      >
                        {word.text}
                      </span>
                    </span>
                    {word.separator}
                    {lastResolvedWordId === word.id ? (
                      <span className="cs-typing-caret ml-0.5 inline-block h-[1.15em] w-[2px] align-[-0.18em] bg-[#111111]" />
                    ) : null}
                  </Fragment>
                );
              })}
            </p>
          ))}
        </div>
      </div>

      {showGlassTransition ? (
        <div
          aria-hidden="true"
          className={`${showPreSoapPulse ? "cs-pre-soep-overlay " : ""}pointer-events-none absolute inset-0 rounded-[28px] bg-white/75 backdrop-blur-[4px]`}
          style={overlayIntroOpacity !== undefined ? { opacity: overlayIntroOpacity } : undefined}
        />
      ) : null}

      {structuredNotesOpacity > 0.02 ? (
        <div
          className="absolute inset-0 z-10 overflow-hidden px-3 pb-6 pt-4 pr-3 text-[11px] leading-[1.55] text-black sm:px-7 sm:pb-10 sm:pt-7 sm:pr-5 sm:text-[14px] sm:leading-[1.6]"
          style={{ opacity: structuredNotesOpacity }}
        >
          <div className="space-y-4">
            {soapSections.map((section, sectionIndex) => (
              <p
                key={`merged-soap-${section.label}`}
                className="max-w-[560px] transition-all duration-200"
                style={{
                  opacity: sectionIndex < visibleStructuredSections ? 1 : 0,
                  transform: `translateY(${sectionIndex < visibleStructuredSections ? 0 : 8}px)`,
                }}
              >
                <strong className="font-semibold text-[#111111]">{section.label}: </strong>
                {section.body}
              </p>
            ))}
          </div>
        </div>
      ) : null}

      <div className="pointer-events-none absolute bottom-4 left-1/2 z-20 -translate-x-1/2">
        <DictationPill mode={pillMode} />
      </div>
    </div>
  );
}

function ComparisonSection({
  title,
  leftTitle,
  leftSubtitle,
  rightTitle,
  rightSubtitle,
  leftBody,
  variant,
  dictationDemo,
}: {
  title: string;
  leftTitle: string;
  leftSubtitle: string;
  rightTitle: string;
  rightSubtitle: string;
  leftBody: string;
  variant: "capture" | "dictation";
  dictationDemo?: DictationDemoData;
}) {
  const loopSnapshot = useDictationLoop(dictationDemo);
  const useDictationHeadingStyle = variant === "dictation";

  return (
    <section className="-mx-4 max-w-[1120px] px-2 py-16 sm:mx-auto sm:px-8 lg:py-20">
      {title ? <SectionHeading title={title} /> : null}
      <div className={`${title ? "mt-10" : "mt-0"} grid grid-cols-2 gap-3 sm:gap-8`}>
        <div className="flex h-full flex-col">
          <ComparisonLabel
            title={leftTitle}
            subtitle={useDictationHeadingStyle ? "" : leftSubtitle}
            tone={useDictationHeadingStyle ? "muted" : "default"}
          />
          <RawTextFrame body={leftBody} dictationDemo={dictationDemo} loopSnapshot={loopSnapshot} />
        </div>
        <div className="flex h-full flex-col">
          <ComparisonLabel
            title={useDictationHeadingStyle ? `${rightTitle} ${rightSubtitle}`.trim() : rightTitle}
            subtitle={useDictationHeadingStyle ? "" : rightSubtitle}
          />
          <StructuredFrame variant={variant} dictationDemo={dictationDemo} loopSnapshot={loopSnapshot} />
        </div>
      </div>
    </section>
  );
}

function TimeSavedSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    let animationFrame = 0;
    let hasAnimated = false;

    const startAnimation = () => {
      if (hasAnimated) return;
      hasAnimated = true;

      const duration = 1800;
      const startTime = performance.now();

      const tick = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        const nextValue = Math.round(easedProgress * TIME_SAVED.value);

        setDisplayValue(nextValue);

        if (progress < 1) {
          animationFrame = window.requestAnimationFrame(tick);
        }
      };

      animationFrame = window.requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          startAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.45 },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="mx-auto max-w-[1120px] px-6 py-8 sm:px-8 lg:py-12">
      <SectionHeading title="Tijdswinst" tone="onDark" />
      <div className="mx-auto mt-8 max-w-[520px] text-center">
        <div className="inline-flex items-end gap-3 rounded-[32px] border border-[#EEF4F8] bg-white px-8 py-6 shadow-[0_22px_54px_rgb(0_0_0_/_0.12)]">
          <span className="pb-2 text-[17px] font-medium text-[var(--cs-navy)] sm:text-[20px]">tot</span>
          <span className="text-[52px] font-semibold leading-none tracking-[-0.08em] text-[var(--cs-navy)] sm:text-[72px]">
            {displayValue}
          </span>
          <span className="pb-2 text-[17px] font-medium text-[var(--cs-navy)] sm:text-[20px]">min</span>
        </div>
        <p className="mt-5 text-[16px] leading-7 text-white">{TIME_SAVED.label}</p>
      </div>
    </section>
  );
}

function WhySlideSection() {
  return (
    <section className="bg-[var(--cs-navy)] px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6">
      <div className="rounded-[28px] bg-[#EEF4F8] py-12 sm:py-16">
        <div className="mx-auto max-w-[1120px] px-6 sm:px-8">
          <SectionHeading title={WHY_SLIDE.title} tone="onLight" />
          {WHY_SLIDE.subtitle ? (
            <div className="mx-auto mt-4 max-w-[820px] text-center text-[15px] leading-7 text-[var(--cs-navy)] sm:text-[17px] sm:leading-8">
              {WHY_SLIDE.subtitle}
            </div>
          ) : null}

          <div className="-mx-6 mt-10 overflow-x-auto pb-2 sm:-mx-8 lg:mx-0 lg:overflow-visible lg:pb-0">
            <div className="grid w-max auto-cols-[minmax(230px,70vw)] grid-flow-col gap-4 px-6 sm:auto-cols-[minmax(320px,42vw)] sm:gap-5 sm:px-8 lg:w-auto lg:grid-flow-row lg:grid-cols-3 lg:auto-cols-auto lg:px-0">
              {WHY_SLIDE.points.map((point) => (
                <div
                  key={point.title}
                  className="rounded-[26px] border border-[#EEF4F8] bg-white p-5 shadow-[0_6px_14px_rgb(12_63_107_/_0.05)] sm:p-6"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] bg-[#EEF4F8]">
                      <img
                        src={point.iconSrc}
                        alt={point.iconAlt}
                        className="h-8 w-8 object-contain"
                      />
                    </div>
                    <h3 className="text-[18px] font-semibold leading-tight text-[var(--cs-navy)] sm:text-[20px]">
                      {point.title}
                    </h3>
                  </div>
                  <p className="mt-5 text-[14px] leading-6 text-black/70 sm:text-[15px] sm:leading-7">{point.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto mt-12 max-w-[1120px] px-6 sm:mt-14 sm:px-8">
          <h3 className="mx-auto text-balance text-center text-[24px] font-semibold leading-[1.1] text-[var(--cs-title-navy)] sm:text-[32px] sm:leading-[1.05] lg:whitespace-nowrap lg:text-[38px] xl:text-[44px]">
            Ontwikkeld samen met medische specialisten
          </h3>
          <ul className="mx-auto mt-8 flex max-w-[1024px] flex-wrap justify-center gap-2.5 sm:mt-10 sm:gap-3">
            {WHY_SLIDE.quotes.map((quote) => (
              <li key={quote.text}>
                <blockquote className="rounded-[18px] border border-[#E3EDF4] bg-white px-4 py-2.5 text-[13px] font-semibold leading-5 text-[var(--cs-title-navy)] shadow-[0_4px_10px_rgb(12_63_107_/_0.04)] sm:px-5 sm:py-3 sm:text-[14px] sm:leading-6">
                  “{quote.text}”
                </blockquote>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const workflowCards = [
    {
      title: "Dicteer of laat meeluisteren tijdens consultatie",
      iconSrc: "/marketing-icons/Afbeelding10.png",
      iconAlt: "Dicteren tijdens consultatie icoon",
      stepNumber: 1,
    },
    {
      title: "De AI behoudt het dictaat of kiest gepaste structuur",
      iconSrc: "/marketing-icons/Afbeelding3.png",
      iconAlt: "AI structureert dictaat icoon",
      stepNumber: 2,
    },
    {
      title: "Tekst wordt geplakt waar je cursor staat",
      iconSrc: "/marketing-icons/Afbeelding19.png",
      iconAlt: "Cursorpositie invoegen icoon",
      stepNumber: 3,
    },
    {
      title: "Carmen opent automatisch op de achtergrond",
      iconSrc: "/carmen-icon.png",
      iconAlt: "Carmen openen icoon",
      supportPosition: "start",
    },
    {
      title: "Dictaten en consulten synchroniseren automatisch tussen mobiel en desktop",
      iconSrc: "/marketing-icons/afbeelding18.png",
      iconAlt: "Apparaten synchroniseren icoon",
      supportPosition: "end",
    },
  ];

  return (
    <section className="bg-[var(--cs-navy)] px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6">
      <div className="rounded-[28px] bg-[#EEF4F8] py-12 sm:py-16">
        <div className="mx-auto max-w-[1280px] px-6 sm:px-8">
          <SectionHeading title="Hoe werkt het" tone="onLight" />
          <div className="mx-auto mt-4 max-w-[820px] text-center text-[15px] leading-7 text-[var(--cs-navy)] sm:text-[17px] sm:leading-8">
            Download de apps en begin. Geen code integratie of nieuwe interface nodig.
          </div>

          <div className="-mx-6 mt-14 overflow-x-auto pb-2 sm:-mx-8 lg:mx-0 lg:overflow-visible lg:pb-0">
            <div className="grid w-max auto-cols-[minmax(230px,70vw)] grid-flow-col gap-4 px-6 sm:auto-cols-[minmax(320px,42vw)] sm:gap-5 sm:px-8 lg:w-auto lg:grid-flow-row lg:grid-cols-6 lg:auto-cols-auto lg:px-0">
              {workflowCards.map((card) => (
                <div
                  key={card.title}
                  className={`rounded-[26px] border border-[#EEF4F8] bg-white px-5 py-5 shadow-[0_6px_14px_rgb(12_63_107_/_0.05)] sm:px-6 sm:py-6 lg:col-span-2 ${
                    card.supportPosition === "start" ? "lg:col-start-2" : ""
                  }`}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-[20px] bg-[#EEF4F8] text-[var(--cs-navy)] sm:h-16 sm:w-16">
                    <img src={card.iconSrc} alt={card.iconAlt} className="h-9 w-9 object-contain" />
                  </div>
                  {card.stepNumber ? (
                    <div className="mt-5 text-[12px] font-semibold uppercase tracking-[0.18em] text-[var(--cs-navy)]">
                      Stap {card.stepNumber}
                    </div>
                  ) : null}
                  <h3
                    className={`${card.stepNumber ? "mt-2" : "mt-5"} text-[19px] font-semibold leading-[1.15] text-[var(--cs-navy)] sm:text-[22px] xl:text-[24px]`}
                  >
                    {card.title}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto mt-12 max-w-[1120px] px-6 sm:mt-14 sm:px-8">
          <SectionHeading title="Dictatie werkt in elke applicatie" tone="onLight" />
          <p className="mx-auto mt-4 max-w-[860px] text-center text-[15px] leading-7 text-[var(--cs-navy)] sm:text-[17px] sm:leading-8">
            Beweeg van consultcaptatie naar verslag en vervolgactie in de tools die vandaag al gebruikt worden.
          </p>
          <div className="relative mx-auto mt-8 w-[88%] overflow-hidden py-4 sm:w-[72%] lg:w-[60%]">
            <div className="cs-app-marquee cs-app-marquee--fast flex w-max items-center motion-reduce:animate-none">
              <AppStripRow />
              <AppStripRow ariaHidden />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AppTile({ item }: { item: AppStripItem }) {
  if (item.type === "image") {
    return (
      <div className="group flex h-[76px] w-[76px] items-center justify-center transition-transform duration-300 hover:-translate-y-1">
        {item.boxed ? (
          <div className="flex h-[64px] w-[64px] items-center justify-center overflow-hidden rounded-[14px] bg-[#03285F] transition-transform duration-300 group-hover:scale-110">
            <img
              src={item.src}
              alt={item.alt}
              className="h-[55px] w-[55px] translate-x-[2px] object-contain"
            />
          </div>
        ) : (
          <img
            src={item.src}
            alt={item.alt}
            className={`${item.compact ? "h-[56px] w-[56px]" : "h-[76px] w-[76px]"} object-contain transition-transform duration-300 group-hover:scale-110`}
          />
        )}
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-center transition-transform duration-300 hover:-translate-y-1">
      <div
        className="flex h-[48px] w-[48px] items-center justify-center rounded-[15px] border border-white text-[17px] font-semibold leading-none tracking-[-0.02em] shadow-[0_18px_30px_rgb(0_0_0_/_0.10)] transition-transform duration-300 group-hover:scale-110 group-hover:shadow-[0_24px_36px_rgb(0_0_0_/_0.16)]"
        style={{ background: item.background, color: item.color }}
        aria-label={item.alt}
        title={item.alt}
      >
        {item.label}
      </div>
    </div>
  );
}

function AppStripRow({ ariaHidden = false }: { ariaHidden?: boolean }) {
  return (
    <div className="flex items-center gap-4 px-2 sm:gap-6" aria-hidden={ariaHidden}>
      {APP_STRIP_ITEMS.map((item, index) => (
        <AppTile key={`${item.type}-${item.alt}-${index}`} item={item} />
      ))}
    </div>
  );
}

function formatPricingAmount(value: number) {
  return value.toLocaleString("nl-BE", {
    maximumFractionDigits: 0,
  });
}

type BillingCycle = "monthly" | "yearly";

const YEARLY_DISCOUNT = 0.1;

function PlatformIcons({ compact = false }: { compact?: boolean } = {}) {
  const appleCls = compact ? "h-[15px] w-[15px]" : "h-[18px] w-[18px]";
  const windowsCls = compact ? "h-[12px] w-[12px]" : "h-[15px] w-[15px]";

  return (
    <span className="flex shrink-0 items-center gap-1.5" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="currentColor" className={appleCls}>
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
      <svg viewBox="0 0 24 24" fill="currentColor" className={windowsCls}>
        <path d="M0 3.449L9.75 2.1v9.451H0V3.449zM10.949 1.95l12.8-1.949v11.5h-12.8V1.95zM0 12.6h9.75v9.451L0 20.699V12.6zm10.949 0h12.8V24l-12.8-1.949V12.6z" />
      </svg>
    </span>
  );
}

function PricingPlanCard({
  plan,
  billingCycle,
}: {
  plan: PricingPlanData;
  billingCycle: BillingCycle;
}) {
  const isContactPlan = plan.monthlyPrice === null;
  const isYearly = billingCycle === "yearly";
  const displayedMonthlyPrice =
    plan.monthlyPrice === null
      ? null
      : isYearly
        ? plan.monthlyPrice * (1 - YEARLY_DISCOUNT)
        : plan.monthlyPrice;
  const yearlyPrice = displayedMonthlyPrice === null ? null : displayedMonthlyPrice * 12;
  const cardClassName = plan.highlighted
    ? "border-[var(--cs-title-navy)] ring-4 ring-white shadow-[0_22px_48px_rgb(12_63_107_/_0.16)]"
    : "border-white shadow-[0_8px_20px_rgb(12_63_107_/_0.08)]";
  const ctaEvent = isContactPlan ? "cta_pricing_contact" : "cta_pricing_start";

  return (
    <article
      className={`relative flex h-full min-w-0 snap-center flex-col rounded-[28px] border-2 bg-white px-5 pb-5 pt-7 sm:px-6 sm:pb-6 ${cardClassName}`}
      aria-label={`${plan.name} prijsplan`}
    >
      {plan.badge ? (
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-[var(--cs-title-navy)] px-5 py-2 text-[12px] font-semibold uppercase tracking-[0.09em] text-white shadow-[0_10px_24px_rgb(12_63_107_/_0.22)]">
          {plan.badge}
        </div>
      ) : null}

      <h3 className="text-[24px] font-semibold leading-none tracking-[-0.03em] text-[var(--cs-title-navy)] sm:text-[30px]">
        {plan.name}
      </h3>

      <div className="mt-7 min-h-[86px] text-[var(--cs-title-navy)]">
        {displayedMonthlyPrice === null ? (
          <div className="pt-2 text-[26px] font-semibold leading-tight tracking-[-0.04em] sm:text-[34px]">
            Prijs op maat
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2">
              <span className="pb-1 text-[17px] font-semibold sm:text-[20px]">€</span>
              <span className="text-[44px] font-semibold leading-none tracking-[-0.06em] sm:text-[58px]">
                {formatPricingAmount(displayedMonthlyPrice)}
              </span>
              <span className="pb-1.5 text-[13px] font-medium text-black/60">
                / maand · excl. btw
              </span>
            </div>
            <div className="mt-1 text-[12px] font-medium text-black/55">
              {isYearly && yearlyPrice !== null
                ? `per gebruiker · € ${formatPricingAmount(yearlyPrice)} per jaar gefactureerd`
                : "per gebruiker"}
            </div>
          </>
        )}
      </div>

      <div className="mt-4 flex min-h-[48px] items-center rounded-[16px] bg-[#EEF4F8] px-4 py-2 text-[12px] font-semibold uppercase leading-5 tracking-[0.08em] text-[var(--cs-title-navy)]">
        {plan.audience}
      </div>

      <div className="mt-6 border-t border-[#DCE8F0] pt-5">
        <p className="text-[13px] font-semibold text-[var(--cs-title-navy)]">
          {plan.featuresIntro}
        </p>
        <ul className="mt-3 space-y-3 text-[13px] leading-5 text-black/70">
          {plan.features.map((feature) => (
            <li key={feature} className="flex gap-2.5">
              <span
                aria-hidden="true"
                className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[var(--cs-title-navy)] text-white"
              >
                <svg className="h-2.5 w-2.5" viewBox="0 0 16 16" fill="none">
                  <path
                    d="m3.25 8.3 3 3.05 6.5-6.7"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto pt-7">
        {plan.note ? (
          <p className="mb-2 px-1 text-[11px] leading-5 text-black/55">{plan.note}</p>
        ) : null}
        <Link
          to={plan.ctaHref}
          aria-label={`${plan.ctaLabel} — ${plan.name}`}
          onClick={() => trackEvent(ctaEvent, { plan: plan.name })}
          className="cs-cta-button inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold leading-5 text-white"
        >
          {isContactPlan ? null : <PlatformIcons compact />}
          <span>{plan.ctaLabel}</span>
        </Link>
      </div>
    </article>
  );
}

function PricingSection() {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("yearly");

  return (
    <section
      id="pricing"
      className="scroll-mt-[132px] bg-[var(--cs-navy)] px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6"
    >
      <div className="rounded-[28px] bg-[#EEF4F8] py-12 sm:py-16 lg:py-20">
        <div className="mx-auto max-w-[1120px] px-6 text-center sm:px-8">
          <SectionHeading title="Prijzen" tone="onLight" />
          <div
            className="mx-auto mt-5 inline-flex rounded-full border border-[#DCE8F0] bg-white/80 p-0.5"
            role="group"
            aria-label="Facturatieperiode"
          >
            <button
              type="button"
              aria-pressed={billingCycle === "monthly"}
              onClick={() => setBillingCycle("monthly")}
              className={`h-8 rounded-full px-4 text-[12px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--cs-title-navy)] ${
                billingCycle === "monthly"
                  ? "bg-[var(--cs-title-navy)] text-white"
                  : "text-[var(--cs-title-navy)] hover:bg-[#EEF4F8]"
              }`}
            >
              Maandelijks
            </button>
            <button
              type="button"
              aria-pressed={billingCycle === "yearly"}
              onClick={() => setBillingCycle("yearly")}
              className={`flex h-8 items-center gap-1 rounded-full py-1 pl-3 pr-1 text-[12px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--cs-title-navy)] ${
                billingCycle === "yearly"
                  ? "bg-[var(--cs-title-navy)] text-white"
                  : "text-[var(--cs-title-navy)] hover:bg-[#EEF4F8]"
              }`}
            >
              <span>Jaarlijks</span>
              <span
                className={`flex h-6 items-center rounded-full px-2 text-[10px] font-bold ${
                  billingCycle === "yearly"
                    ? "bg-white text-[var(--cs-title-navy)]"
                    : "bg-[#DDEAF2] text-[var(--cs-title-navy)]"
                }`}
              >
                −10%
              </span>
            </button>
          </div>
        </div>

        <div className="mx-auto mt-12 max-w-[1120px] px-6 pt-3 sm:px-8">
          <div
            className="-mx-6 snap-x snap-mandatory overflow-x-auto pb-5 pt-3 sm:-mx-8 lg:mx-0 lg:overflow-visible"
            aria-label="Prijsplannen"
            tabIndex={0}
          >
            <div className="grid w-max auto-cols-[minmax(300px,84vw)] grid-flow-col items-stretch gap-5 px-6 sm:auto-cols-[minmax(330px,44vw)] sm:px-8 lg:w-auto lg:grid-flow-row lg:grid-cols-3 lg:auto-cols-auto lg:px-0">
              {PRICING_PLANS.map((plan) => (
                <PricingPlanCard key={plan.name} plan={plan} billingCycle={billingCycle} />
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

function PrivacyCard({
  title,
  body,
  iconSrc,
  iconAlt,
}: {
  title: string;
  body: string;
  iconSrc: string;
  iconAlt: string;
}) {
  return (
    <div className="rounded-[26px] border border-[#EEF4F8] bg-white p-5 shadow-[0_6px_14px_rgb(12_63_107_/_0.05)] sm:p-6">
      <div className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-[#EEF4F8]">
        <img src={iconSrc} alt={iconAlt} className="h-8 w-8 object-contain" />
      </div>
      <h3 className="mt-5 text-[18px] font-semibold leading-tight text-[var(--cs-navy)] sm:text-[20px]">{title}</h3>
      <p className="mt-3 text-[14px] leading-6 text-black/70 sm:text-[15px] sm:leading-7">{body}</p>
    </div>
  );
}

function HeroAppDock({ large = false }: { large?: boolean } = {}) {
  const leadingApps = [
    { src: "/landing-app-icons/mail.png", alt: "Mail" },
    { src: "/landing-app-icons/chrome.png", alt: "Chrome" },
  ];
  const trailingApps = [
    { src: "/landing-app-icons/word.png", alt: "Word" },
    { src: "/landing-app-icons/notes.png", alt: "Notes" },
  ];

  const iconHover =
    "transition-transform duration-200 ease-out hover:-translate-y-2 hover:scale-110 motion-reduce:transition-none motion-reduce:hover:transform-none";

  const containerCls = large
    ? "gap-2 px-3 py-3 sm:gap-2.5 sm:px-4 sm:py-4"
    : "gap-1 px-3 py-1.5 sm:gap-1.5 sm:px-4 sm:py-2";
  const outerIconCls = large ? "h-[52px] w-[52px] sm:h-[72px] sm:w-[72px] md:h-[88px] md:w-[88px]" : "h-9 w-9 sm:h-10 sm:w-10";
  const innerIconCls = large ? "h-[52px] w-[52px] sm:h-[72px] sm:w-[72px] md:h-[88px] md:w-[88px]" : "h-9 w-9 sm:h-10 sm:w-10";
  const tileCls = large ? "h-[52px] w-[52px] sm:h-[72px] sm:w-[72px] md:h-[88px] md:w-[88px]" : "h-9 w-9 sm:h-10 sm:w-10";
  const carmenImgCls = large
    ? "h-[45px] w-[45px] translate-x-[2px] object-contain sm:h-[62px] sm:w-[62px] sm:translate-x-[3px] md:h-[76px] md:w-[76px] md:translate-x-[4px]"
    : "h-[31px] w-[31px] translate-x-[2px] object-contain sm:h-[34px] sm:w-[34px]";
  const tooltipCls = large ? "-top-10 sm:-top-12" : "-top-9 sm:-top-10";
  const tooltipText = "px-2.5 py-1 text-[12px]";

  return (
    <div
      className={`relative flex items-end justify-center rounded-full border border-[#EEF4F8] bg-white shadow-[0_20px_44px_rgb(0_0_0_/_0.16)] ${containerCls}`}
      style={{
        background: "#ffffff",
      }}
    >
      {leadingApps.map((app, idx) => (
        <img
          key={app.alt}
          src={app.src}
          alt={app.alt}
          className={`relative select-none drop-shadow-[0_6px_13px_rgb(0_0_0_/_0.20)] ${idx === 0 ? outerIconCls : innerIconCls} ${iconHover}`}
          draggable={false}
        />
      ))}

      {/* Carmen — running app, with cursor click animation */}
      <div className={`relative ${iconHover}`}>
        <div className={`pointer-events-none absolute left-1/2 -translate-x-1/2 ${tooltipCls}`}>
          <div className={`relative whitespace-nowrap rounded-lg border border-[#EEF4F8] bg-white font-semibold text-[var(--cs-navy)] shadow-[0_5px_14px_rgb(0_0_0_/_0.18)] ${tooltipText}`}>
            Carmen
            <span
              aria-hidden="true"
              className="absolute left-1/2 top-full -mt-1 h-2 w-2 -translate-x-1/2 rotate-45 border-b border-r border-white/80 bg-white"
            />
          </div>
        </div>

        <div className={`relative flex items-center justify-center overflow-hidden ${large ? "rounded-[20px]" : "rounded-[10px]"} bg-[#03285F] shadow-[0_9px_24px_rgb(0_0_0_/_0.18)] ${tileCls}`}>
          <img
            src="/carmen-icon-inverted.png"
            alt="Carmen"
            className={`object-contain ${carmenImgCls}`}
            draggable={false}
          />
          {/* Click dim — translucent black overlay flashes on each click */}
          <div
            className="carmen-tile-flash pointer-events-none absolute inset-0 bg-black opacity-0 motion-reduce:animate-none"
            aria-hidden="true"
          />
        </div>

        {/* Running-app indicator dot */}
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute left-1/2 -translate-x-1/2 rounded-full bg-black ${large ? "-bottom-2 h-1.5 w-1.5" : "-bottom-1.5 h-1 w-1"}`}
        />

        {/* Mouse cursor — travels in from outside, hovers, clicks, exits */}
        <div className="carmen-cursor pointer-events-none absolute bottom-[24%] right-[22%] opacity-0 motion-reduce:animate-none">
          <svg
            width={large ? 22 : 14}
            height={large ? 27 : 17}
            viewBox="0 0 26 30"
            fill="none"
            className="drop-shadow-[0_2px_4px_rgb(0_0_0_/_0.45)]"
            aria-hidden="true"
          >
            <path
              d="M4 3 L4 24 L10 19 L13.5 27 L17 25.5 L13.5 17.5 L22 17.5 Z"
              fill="black"
              stroke="white"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {trailingApps.map((app, idx) => (
        <img
          key={app.alt}
          src={app.src}
          alt={app.alt}
          className={`relative select-none drop-shadow-[0_6px_13px_rgb(0_0_0_/_0.20)] ${idx === 0 ? innerIconCls : outerIconCls} ${iconHover}`}
          draggable={false}
        />
      ))}
    </div>
  );
}

function FaqItem({
  question,
  answer,
  open,
  onToggle,
}: {
  question: string;
  answer: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-[#EEF4F8]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 py-6 text-left sm:py-7"
      >
        <h3 className="text-[18px] font-semibold leading-tight text-white sm:text-[21px]">
          {question}
        </h3>
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#EEF4F8] bg-white text-[var(--cs-navy)] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        >
          <svg
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m5 8 5 5 5-5" />
          </svg>
        </span>
      </button>

      <div
        className={`grid overflow-hidden transition-all duration-300 ease-out ${
          open ? "grid-rows-[1fr] pb-6 opacity-100 sm:pb-7" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="min-h-0">
          <p className="max-w-[840px] text-[15px] leading-7 text-white sm:text-[16px]">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export function CarmenLanding() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(LANDING_NAV_ITEMS[0]?.href ?? "#features");
  const [openFaqQuestion, setOpenFaqQuestion] = useState<string>(FAQS[0]?.question ?? "");

  const auth = useAuth();
  const isAuthenticated = !auth.loading && Boolean(auth.user);
  const primaryCtaTo = isAuthenticated ? "/download" : "/signup";
  const primaryCtaLabel = isAuthenticated ? HERO_CONTENT.primaryCtaAuthed : HERO_CONTENT.primaryCta;

  const sectionIds = useMemo(
    () => LANDING_NAV_ITEMS.map((item) => item.href.replace("#", "")),
    [],
  );

  useEffect(() => {
    const onScroll = () => {
      setNavScrolled(window.scrollY > 16);

      let currentSection = LANDING_NAV_ITEMS[0]?.href ?? "#features";
      for (const id of sectionIds) {
        const element = document.getElementById(id);
        if (!element) continue;

        const top = element.getBoundingClientRect().top;
        if (top <= NAV_SCROLL_OFFSET + 24) {
          currentSection = `#${id}`;
        }
      }

      setActiveSection(currentSection);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sectionIds]);

  const handleAnchorClick = useCallback(
    (href: string) => (event: ReactMouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      event.preventDefault();

      if (href === "#top") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setActiveSection(LANDING_NAV_ITEMS[0]?.href ?? "#features");
        setMobileMenuOpen(false);
        return;
      }

      const target = document.querySelector(href);
      if (!(target instanceof HTMLElement)) return;

      const top = target.getBoundingClientRect().top + window.scrollY - NAV_SCROLL_OFFSET;
      window.scrollTo({ top, behavior: "smooth" });
      setActiveSection(href);
      setMobileMenuOpen(false);
    },
    [],
  );
  return (
    <div id="top" className="cs-marketing bg-[var(--cs-navy)] text-[var(--cs-navy)]">
      <LandingNav
        items={LANDING_NAV_ITEMS}
        activeSection={activeSection}
        mobileMenuOpen={mobileMenuOpen}
        navScrolled={navScrolled}
        onToggleMenu={() => setMobileMenuOpen((open) => !open)}
        onAnchorClick={handleAnchorClick}
      />

      <section className="relative overflow-hidden bg-[var(--cs-navy)] px-2 pt-2 sm:px-4 sm:pt-4 lg:px-6 lg:pt-6">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-2 bottom-0 top-2 rounded-[28px] sm:inset-x-4 sm:top-4 lg:inset-x-6 lg:top-6"
          style={{
            background:
              "linear-gradient(90deg, rgba(252,251,247,0.98) 0%, rgba(243,249,253,0.96) 46%, rgba(220,231,239,0.92) 100%)",
          }}
        />

        <div className="relative z-10 mx-auto flex max-w-[1120px] flex-col items-center px-6 pb-12 pt-24 text-center sm:px-8 sm:pt-28 lg:pb-16 lg:pt-32">
          {/* w-fit wrapper sizes to the title; w-0 min-w-full keeps the subtitle from widening it, so both share one width. */}
          <div className="flex w-fit max-w-full flex-col items-center">
            <h1 className="text-balance text-[34px] font-semibold leading-[1.06] tracking-[-0.04em] text-[var(--cs-title-navy)] sm:text-[52px] md:text-[64px] lg:whitespace-nowrap lg:text-[80px]">
              {HERO_CONTENT.title.split(" ").map((word, i, arr) => (
                <span
                  key={`${word}-${i}`}
                  className="cs-hero-word"
                  style={{ animationDelay: `${i * 90}ms` }}
                >
                  {word}
                  {i < arr.length - 1 ? " " : ""}
                </span>
              ))}
            </h1>

            <p
              className="cs-hero-fade mt-6 w-0 min-w-full text-pretty text-[16px] leading-[1.55] text-[var(--cs-navy)] sm:text-[18px] md:text-[19px]"
              style={{
                animationDelay: `${HERO_CONTENT.title.split(" ").length * 90 + 200}ms`,
              }}
            >
              {HERO_CONTENT.body}
            </p>
          </div>

          <div
            className="cs-hero-fade mt-8 flex w-fit flex-col items-center gap-4 sm:flex-row"
            style={{
              animationDelay: `${HERO_CONTENT.title.split(" ").length * 90 + 500}ms`,
            }}
          >
            {isAuthenticated ? (
              <Link
                to={primaryCtaTo}
                onClick={() => trackEvent("cta_try_carmen", { location: "hero" })}
                className="cs-cta-button cs-cta-button--on-dark inline-flex items-center justify-center gap-2.5 rounded-full bg-[var(--cs-navy)] px-6 py-3 text-[15px] font-medium text-white"
              >
                <span>{primaryCtaLabel}</span>
              </Link>
            ) : null}

            {!isAuthenticated ? (
              <Link
                to={primaryCtaTo}
                onClick={() => trackEvent("cta_signup")}
                className="cs-cta-button cs-cta-button--on-dark inline-flex items-center justify-center gap-2.5 rounded-full bg-[var(--cs-navy)] px-6 py-3 text-[15px] font-medium text-white"
              >
                <PlatformIcons />
                <span>{primaryCtaLabel}</span>
              </Link>
            ) : (
              <a
                href="mailto:info@askcarmen.eu"
                onClick={() => trackEvent("cta_contact")}
                className="cs-cta-button cs-cta-button--soft hidden items-center justify-center gap-2.5 rounded-full bg-white px-6 py-3 text-[15px] font-medium sm:inline-flex"
              >
                <span>Contact</span>
              </a>
            )}
          </div>

          <div
            id="features"
            className="cs-hero-fade mt-10 w-full scroll-mt-[132px] text-left sm:mt-12"
            style={{
              animationDelay: `${HERO_CONTENT.title.split(" ").length * 90 + 750}ms`,
            }}
          >
            <ComparisonSection
              title={COMPARISON_SECTIONS[0].title}
              leftTitle={COMPARISON_SECTIONS[0].leftTitle}
              leftSubtitle={COMPARISON_SECTIONS[0].leftSubtitle}
              rightTitle={COMPARISON_SECTIONS[0].rightTitle}
              rightSubtitle={COMPARISON_SECTIONS[0].rightSubtitle}
              leftBody={COMPARISON_SECTIONS[0].leftBody}
              variant={COMPARISON_SECTIONS[0].variant}
              dictationDemo={COMPARISON_SECTIONS[0].dictationDemo}
            />
          </div>
        </div>
      </section>

      <TimeSavedSection />
      <WhySlideSection />

      <HowItWorksSection />

      <section id="privacy" className="mx-auto max-w-[1120px] scroll-mt-[132px] px-6 py-16 sm:px-8 lg:py-20">
        <SectionHeading title="Privacy by design" tone="onDark" />
        <div className="-mx-6 mt-10 overflow-x-auto pb-2 sm:-mx-8 lg:mx-0 lg:overflow-visible lg:pb-0">
          <div className="grid w-max auto-cols-[minmax(230px,70vw)] grid-flow-col gap-4 px-6 sm:auto-cols-[minmax(320px,42vw)] sm:gap-5 sm:px-8 lg:w-auto lg:grid-flow-row lg:grid-cols-3 lg:auto-cols-auto lg:px-0">
            {PRIVACY_CARDS.map((card) => (
              <PrivacyCard
                key={card.title}
                title={card.title}
                body={card.body}
                iconSrc={card.iconSrc}
                iconAlt={card.iconAlt}
              />
            ))}
          </div>
        </div>
        <nav
          aria-label="Privacy en juridische informatie"
          className="mt-6 flex flex-wrap justify-center gap-x-5 text-[14px] font-medium text-white/80"
        >
          <Link
            to="/privacy"
            className="inline-flex min-h-11 items-center transition-colors hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:underline focus-visible:underline-offset-4"
          >
            Privacy
          </Link>
          <Link
            to="/terms"
            className="inline-flex min-h-11 items-center transition-colors hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:underline focus-visible:underline-offset-4"
          >
            Gebruiksvoorwaarden
          </Link>
          <Link
            to="/trust"
            className="inline-flex min-h-11 items-center transition-colors hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:underline focus-visible:underline-offset-4"
          >
            Trust Center
          </Link>
        </nav>
      </section>

      <PricingSection />

      <section className="mx-auto max-w-[1120px] px-6 py-20 sm:px-8 lg:py-28">
        <SectionHeading title="Bespaar vandaag nog tijd" tone="onDark" />
        <p className="mx-auto mt-5 max-w-[620px] text-center text-[15px] leading-7 text-white sm:text-[17px] sm:leading-8">
          Installeer Carmen op desktop en mobiel en dicteer meteen in je eigen workflow.
        </p>

        <div className="mt-16 flex justify-center">
          <HeroAppDock large />
        </div>

        <div className="mt-14 flex justify-center">
          <Link
            to={primaryCtaTo}
            onClick={() => trackEvent("cta_try_carmen", { location: "bottom" })}
            className="cs-cta-button cs-cta-button--on-dark inline-flex items-center justify-center gap-2.5 rounded-full bg-[var(--cs-navy)] px-8 py-4 text-[16px] font-medium text-white"
          >
            {isAuthenticated ? null : <PlatformIcons />}
            <span>{primaryCtaLabel}</span>
          </Link>
        </div>
      </section>

      <section id="faq" className="mx-auto max-w-[1120px] scroll-mt-[132px] px-6 py-16 sm:px-8 lg:py-20">
        <SectionHeading title="Veelgestelde vragen" tone="onDark" />
        <div className="mt-10 border-t border-[#EEF4F8]">
          {FAQS.map((faq) => (
            <FaqItem
              key={faq.question}
              question={faq.question}
              answer={faq.answer}
              open={openFaqQuestion === faq.question}
              onToggle={() =>
                setOpenFaqQuestion((current) =>
                  current === faq.question ? "" : faq.question,
                )
              }
            />
          ))}
        </div>
      </section>

      <footer className="border-t border-[#EEF4F8] bg-[var(--cs-navy)]">
        <div className="mx-auto max-w-[1320px] px-6 py-16 sm:px-8 lg:px-12 lg:py-20">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)] lg:gap-16">
            <div>
              <Link to="/" className="inline-flex items-center gap-4 text-white">
                <span className="text-3xl font-semibold tracking-tight">Carmen</span>
              </Link>
              <p className="mt-5 max-w-[30ch] text-[16px] leading-7 text-white">
                Medische dictatie en workflow-AI die meeloopt in de tools die teams vandaag al gebruiken.
              </p>
            </div>

            <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
              {FOOTER_GROUPS.map((group) => (
                <div key={group.title}>
                  <div className="text-[13px] font-[900] uppercase tracking-[0.20em] text-white">
                    {group.title}
                  </div>
                  <div className="mt-5 space-y-3">
                    {group.links.map((link) =>
                      link.type === "route" ? (
                        <Link
                          key={`${group.title}-${link.label}`}
                          to={link.href}
                          className="block text-[16px] leading-7 text-white transition-colors hover:text-white"
                        >
                          {link.label}
                        </Link>
                      ) : link.type === "anchor" ? (
                        <button
                          key={`${group.title}-${link.label}`}
                          type="button"
                          onClick={handleAnchorClick(link.href)}
                          className="block text-left text-[16px] leading-7 text-white transition-colors hover:text-white"
                        >
                          {link.label}
                        </button>
                      ) : (
                        <a
                          key={`${group.title}-${link.label}`}
                          href={link.href}
                          className="block text-[16px] leading-7 text-white transition-colors hover:text-white"
                        >
                          {link.label}
                        </a>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14 flex flex-col gap-4 border-t border-[#EEF4F8] pt-8 text-[15px] text-white sm:flex-row sm:items-center sm:justify-between">
            <div>© 2026 Carmen</div>
            <div className="flex items-center gap-4">
              <span>Carmen is lid van het NVIDIA Inception-programma.</span>
              <img
                src="/marketing-icons/nvidia-inception-badge.webp"
                alt="NVIDIA Inception Program"
                width={600}
                height={221}
                loading="lazy"
                className="h-12 w-auto rounded-sm"
              />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
